<template>
  <Teleport to="body">
    <cl-modal
      :close-modal-text="$t('close')"
      :visible="visible"
      :size="'md'"
      test-id="update-customer-modal"
      @on-close="onClose"
    >
      <template #[`header.title`]>
        <cl-heading :size="'h4'"> {{ t("edit-customer") }}</cl-heading>
      </template>
      <div>
        <section class="tw-mt-6 tw-mb-10">
          <cl-stepper :steps="steps" />
        </section>
        <!-- add customer -->
        <customer-step-index
          v-if="customerStep.current"
          v-model:form="customerForm"
          @form-validation="isCustomerFormValid = $event"
          :is-current-step="customerStep.current"
        />
      </div>
      <!-- Add Solutions Step 2 -->
      <solutions-step-index
        v-if="solutionsStep.current"
        :form="solutionsForm"
        @update:form="Object.assign(solutionsForm, $event)"
        @form-validation="isSolutionsFormValid = $event"
        :is-current-step="solutionsStep.current"
        :is-confirmation-banner-visible="isConfirmationBannerVisible"
        :list-licences-msp="listLicencesMsp"
        :is-phish-service-available="hasServiceAvailable(PHISH_SERVICE)"
        :is-phishing-simulation-service-available="
          hasServiceAvailable(PHISHING_SIMULATION)
        "
        :is-sat-service-available="hasServiceAvailable(SAT_SERVICE)"
        :is-arc-service-available="hasServiceAvailable(ARC_SERVICE)"
        :is-confirmation-checked="isConfirmationChecked"
        @toggle-confirm="toggleIsConfirmationChecked"
        @isDisabled="isSolutionsFormDisabled = $event"
      />

      <!-- footer -->
      <template #[`footer.decline`]>
        <cl-button
          data-test-id="edit-customer-btn-cancel"
          :variant="'secondary-ghost'"
          @on-click="onClose"
        >
          <span>{{ t("cancel") }}</span>
        </cl-button>
        <template v-if="solutionsStep.current">
          <cl-button
            :variant="'secondary-outline'"
            data-test-id="update-customer-input-btn-go-back-to-solutions-step"
            @on-click="goToStep(1)"
          >
            <span>{{ t("back") }}</span>
          </cl-button>
        </template>
      </template>
      <template #[`footer.accept`]>
        <template v-if="customerStep.current">
          <cl-button
            variant="secondary"
            data-test-id="update-customer-btn-go-to-customers-step"
            :disabled="isCustomerFormValid"
            @on-click="
              () => {
                customerStep.completed = true;
                goToStep(2);
              }
            "
          >
            <span>{{ t("next") }}</span>
          </cl-button>
        </template>
        <template v-if="solutionsStep.current">
          <div>
            <cl-button v-if="isRequestPending" :variant="'secondary'" disabled>
              <cl-spinner
                :loading-text="$t('loading')"
                :size="'small'"
                class="mr-2"
              />
              <span>{{ t("saving") }}</span>
            </cl-button>
            <cl-button
              v-else-if="isAnySolutionSelected"
              variant="secondary"
              data-test-id="update-save-customer-btn-save-save"
              :disabled="isUpdateCustomerButtonDisabled"
              @on-click="updateCustomer"
            >
              {{ t("save-changes") }}
            </cl-button>
            <div
              v-else
              v-tippy="{
                content: t('select-solution-to-save-changes'),
                placement: 'top',
              }"
            >
              <cl-button
                variant="secondary"
                data-test-id="update-save-customer-btn-save-save"
                :disabled="isUpdateCustomerButtonDisabled"
                @on-click="updateCustomer"
              >
                {{ t("save-changes") }}
              </cl-button>
            </div>
          </div>
        </template>
      </template>
    </cl-modal>
  </Teleport>
</template>
<script setup lang="ts">
import { computed, reactive, ref, toRefs } from "vue";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";
import { useArctitanStore } from "@/store/arctitan.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import {
  ARC_SERVICE,
  PHISH_SERVICE,
  PHISHING_SIMULATION,
  SAT_SERVICE,
  SEG_SERVICE,
} from "@/utils/services";
import { useI18n } from "vue-i18n";
import { TIER } from "@/constants";
import type { Step } from "@cybersentriq/frontend-component-library/dist/types/src/components/stepper/types";
import { useGlobalOrganisationID } from "@/composables/useGlobalOrganisationID";
import { useToggle } from "@vueuse/core";

import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { TUpdateCustomerProps } from "./t-update-customer.types";
import CustomerStepIndex from "./customer-step/customer-step.index.vue";
import SolutionsStepIndex from "./solutions-step/solutions-step.index.vue";
import {
  useRequestStatus,
  PENDING,
  READY,
} from "@/composables/useRequestStatus";
import { useIsSEGCheckboxAvailable } from "@/composables/useFlags/useIsSEGCheckboxAvailable";

const { isSEGCheckboxAvailable } = useIsSEGCheckboxAvailable();

const toastStore = useToastStore();
const isCustomerFormValid = ref<boolean>(false);
const isSolutionsFormValid = ref<boolean>(false);
const { setRequestStatus, isRequestPending } = useRequestStatus({
  type: READY,
});

const props = defineProps<TUpdateCustomerProps>();
const { visible, orginisationObject, listAllLicences, listLicencesMsp } =
  toRefs(props);
const { t } = useI18n();
const organisationsStore = useOrganisationsStore();
const licenceStore = useLicenceStore();
const arctitanStore = useArctitanStore();

const assignSeats = () => {
  return listAllLicences.value.seats_purchased;
};
const getSolutionDetails = (solution_uuid: string) => {
  return listAllLicences.value.licences?.find(
    (licence) => licence.solution_uuid === solution_uuid,
  );
};
const hasServiceAvailable = (solution_uuid: string): boolean => {
  return listAllLicences.value.licences.some(
    (obj) => obj.solution_uuid === solution_uuid || false,
  );
};

const customerForm = ref({
  name: orginisationObject.value.name,
  description: orginisationObject.value.description || "",
  uuid: orginisationObject.value.uuid,
});

const solutionsForm = reactive({
  licensesByGroup: {
    seats_purchased: assignSeats(),
  },
  emailSecurity: {
    get seats_purchased() {
      return solutionsForm.licensesByGroup.seats_purchased;
    },
    expires_at: getSolutionDetails(PHISH_SERVICE)?.expires_at,
    switcher: hasServiceAvailable(PHISH_SERVICE),
  },
  emailArchiving: {
    get seats_purchased() {
      return solutionsForm.licensesByGroup.seats_purchased;
    },
    expires_at: getSolutionDetails(ARC_SERVICE)?.expires_at,
    switcher: hasServiceAvailable(ARC_SERVICE),
  },
  phishingSimulation: {
    get seats_purchased() {
      return solutionsForm.licensesByGroup.seats_purchased;
    },
    expires_at: getSolutionDetails(PHISHING_SIMULATION)?.expires_at,
    switcher: hasServiceAvailable(PHISHING_SIMULATION),
  },
  sat: {
    get seats_purchased() {
      return solutionsForm.licensesByGroup.seats_purchased;
    },
    expires_at: getSolutionDetails(SAT_SERVICE)?.expires_at,
    switcher: hasServiceAvailable(SAT_SERVICE),
  },
  seg: {
    expires_at: getSolutionDetails(SEG_SERVICE)?.expires_at,
    switcher: hasServiceAvailable(SEG_SERVICE),
  },
});

const customerStep = ref<Step>({
  index: 1,
  label: t("edit-customer"),
  current: true,
  completed: false,
  actionable: false,
});
const solutionsStep = ref<Step>({
  index: 2,
  label: t("step-edit-solutions"),
  current: false,
  completed: false,
  actionable: false,
});
const steps = computed<Step[]>(() => [customerStep.value, solutionsStep.value]);
const stepsRefs = [customerStep, solutionsStep];
const { mspGlobalOrganisationId } = useGlobalOrganisationID();
const isSolutionsFormDisabled = ref(false);

const goToStep = (index: number | string) => {
  for (const step of stepsRefs) {
    if (index === step.value.index) {
      step.value.current = true;
      step.value.completed = false;

      continue;
    }

    step.value.current = false;
  }
};
const licenceUpdated = ref(false);
const updateCustomer = async () => {
  try {
    setRequestStatus({ type: PENDING });
    await organisationsStore.updateOrg({
      formData: { ...customerForm.value },
      parent_uuid: mspGlobalOrganisationId.value,
      organisation_type: TIER.customer,
    });
  } finally {
    if (
      solutionsForm.emailSecurity.switcher &&
      hasServiceAvailable(PHISH_SERVICE)
    ) {
      if (!licenceUpdated.value) {
        licenceUpdated.value = true;
        await updateEmailSecurityLicenses();
      }
    } else if (hasServiceAvailable(PHISH_SERVICE)) {
      await removeLicenses(
        getSolutionDetails(PHISH_SERVICE)?.uuid || "",
        PHISH_SERVICE,
      );
    } else if (
      solutionsForm.emailSecurity.switcher &&
      !hasServiceAvailable(PHISH_SERVICE)
    ) {
      await createEmailSecurityLicenses();
    }
    if (isSEGCheckboxAvailable.value) {
      const segCheckboxChanged =
        solutionsForm.seg.switcher !== hasServiceAvailable(SEG_SERVICE);

      if (segCheckboxChanged) {
        if (solutionsForm.seg.switcher) {
          await createSEGLicenses();
        } else {
          const segServiceUuid = getSolutionDetails(SEG_SERVICE)?.uuid;
          if (segServiceUuid) {
            await removeLicenses(segServiceUuid, SEG_SERVICE);
          }
        }
      }
    }

    if (
      solutionsForm.phishingSimulation.switcher &&
      hasServiceAvailable(PHISHING_SIMULATION)
    ) {
      if (!licenceUpdated.value) {
        licenceUpdated.value = true;
        await updatePhishingSimLicenses();
      }
    } else if (hasServiceAvailable(PHISHING_SIMULATION)) {
      await removeLicenses(
        getSolutionDetails(PHISHING_SIMULATION)?.uuid || "",
        PHISHING_SIMULATION,
      );
    } else if (
      solutionsForm.phishingSimulation.switcher &&
      !hasServiceAvailable(PHISHING_SIMULATION)
    ) {
      await createPhishingSimLicenses();
    }

    if (solutionsForm.sat.switcher && hasServiceAvailable(SAT_SERVICE)) {
      if (!licenceUpdated.value) {
        licenceUpdated.value = true;
        await updateSATLicenses();
      }
    } else if (hasServiceAvailable(SAT_SERVICE)) {
      await removeLicenses(
        getSolutionDetails(SAT_SERVICE)?.uuid || "",
        SAT_SERVICE,
      );
    } else if (
      solutionsForm.sat.switcher &&
      !hasServiceAvailable(SAT_SERVICE)
    ) {
      await createSATLicenses();
    }

    if (
      solutionsForm.emailArchiving.switcher &&
      hasServiceAvailable(ARC_SERVICE)
    ) {
      try {
        await arctitanStore.hasTagCustomer({
          formData: { ...customerForm.value },
          uuid: orginisationObject.value.uuid,
        });
      } catch (e) {
        /* eslint-disable no-console */
        console.log(e);
      } finally {
        if (!licenceUpdated.value) {
          licenceUpdated.value = true;
          await updateEmailArchivingLicenses();
        }
      }
    } else if (hasServiceAvailable(ARC_SERVICE)) {
      await removeLicenses(
        getSolutionDetails(ARC_SERVICE)?.uuid || "",
        ARC_SERVICE,
      );
    } else if (
      solutionsForm.emailArchiving.switcher &&
      !hasServiceAvailable(ARC_SERVICE)
    ) {
      try {
        await arctitanStore.hasTagCustomer({
          formData: { ...customerForm.value },
          uuid: mspGlobalOrganisationId.value,
        });
        await arctitanStore.createCustomerArctian({
          formData: { ...customerForm.value },
          uuid: mspGlobalOrganisationId.value,
        });
      } catch (e) {
        /* eslint-disable no-console */
        console.log(e);
      } finally {
        await createEmailArchivingLicenses();
      }
    }

    await finished();
  }
};

const updateEmailSecurityLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(PHISH_SERVICE)?.uuid,
      formData: solutionsForm.emailSecurity,
      uuid: orginisationObject.value.uuid,
      solution_uuid: PHISH_SERVICE,
    });
  } catch {
    setRequestStatus({ type: READY });
  }
};
const removeLicenses = async (licence_uuid: string, solution_uuid: string) => {
  try {
    await licenceStore.removeLicence({
      licence_uuid: licence_uuid,
      uuid: orginisationObject.value.uuid,
      solution_uuid: solution_uuid,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const updatePhishingSimLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(PHISHING_SIMULATION)?.uuid,
      formData: solutionsForm.phishingSimulation,
      uuid: orginisationObject.value.uuid,
      solution_uuid: PHISHING_SIMULATION,
    });
  } catch {
    setRequestStatus({ type: READY });
  }
};
const updateSATLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(SAT_SERVICE)?.uuid,
      formData: solutionsForm.sat,
      uuid: orginisationObject.value.uuid,
      solution_uuid: SAT_SERVICE,
    });
  } catch {
    setRequestStatus({ type: READY });
  }
};
const updateEmailArchivingLicenses = async () => {
  try {
    await licenceStore.updateLicence({
      licence_uuid: getSolutionDetails(ARC_SERVICE)?.uuid,
      formData: solutionsForm.emailArchiving,
      uuid: orginisationObject.value.uuid,
      solution_uuid: ARC_SERVICE,
    });
  } catch {
    setRequestStatus({ type: READY });
  }
};
const createEmailSecurityLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: PHISH_SERVICE,
      formData: solutionsForm.emailSecurity,
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.customer,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createSEGLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: SEG_SERVICE,
      formData: {
        ...solutionsForm.seg,
        seats_purchased: solutionsForm.emailSecurity.seats_purchased,
      },
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.customer,
      shouldCallV1: false,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createPhishingSimLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: PHISHING_SIMULATION,
      formData: solutionsForm.phishingSimulation,
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.customer,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createSATLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: SAT_SERVICE,
      formData: solutionsForm.sat,
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.customer,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};
const createEmailArchivingLicenses = async () => {
  try {
    await licenceStore.createLicence({
      solution_uuid: ARC_SERVICE,
      formData: solutionsForm.emailArchiving,
      uuid: orginisationObject.value.uuid,
      organisation_type: TIER.customer,
    });
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
  }
};

const finished = async () => {
  setRequestStatus({ type: READY });
  emit("on-update");

  toastStore.displayToast({
    title: t("success"),
    message: t("changes-saved"),
    duration: 5000,
    variant: "success",
    testId: "update-customer-toast-success",
  });
};
const emit = defineEmits<{
  (event: "on-close"): void;
  (event: "on-update"): void;
}>();
const onClose = () => {
  emit("on-close");
};

const [isConfirmationChecked, toggleIsConfirmationChecked] = useToggle(false);
const isConfirmationBannerVisible = computed(() => {
  if (!hasServiceAvailable(PHISH_SERVICE)) {
    return false;
  }

  return !solutionsForm.emailSecurity.switcher;
});
const isAnySolutionSelected = computed(
  () =>
    solutionsForm.emailSecurity.switcher ||
    solutionsForm.phishingSimulation.switcher ||
    solutionsForm.sat.switcher ||
    solutionsForm.emailArchiving.switcher,
);
const isUpdateCustomerButtonDisabled = computed(() => {
  if (isConfirmationBannerVisible.value && !isConfirmationChecked.value) {
    return true;
  }

  return isSolutionsFormDisabled.value || !isAnySolutionSelected.value;
});
</script>
